import { useMemo, useState } from "react";
import TitleCard from "@/components/feature/TitleCard";
import Pagination from "@/components/base/Pagination";
import FilterGroup from "@/pages/category/components/FilterGroup";
import type { TitleItem } from "@/types/content";

const ALL = "全部";
const PAGE_SIZE = 8;

interface CategoryBrowserProps {
  items: TitleItem[];
  categoryLabel: string;
}

type SortMode = "latest" | "hot";

export default function CategoryBrowser({ items, categoryLabel }: CategoryBrowserProps) {
  const [genre, setGenre] = useState(ALL);
  const [region, setRegion] = useState(ALL);
  const [year, setYear] = useState(ALL);
  const [sort, setSort] = useState<SortMode>("latest");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const genreOptions = useMemo(
    () => [ALL, ...Array.from(new Set(items.flatMap((item) => item.genres)))],
    [items],
  );
  const regionOptions = useMemo(
    () => [ALL, ...Array.from(new Set(items.map((item) => item.region)))],
    [items],
  );
  const yearOptions = useMemo(
    () => [ALL, ...Array.from(new Set(items.map((item) => String(item.year)))).sort((a, b) => Number(b) - Number(a))],
    [items],
  );

  const filtered = useMemo(() => {
    const result = items.filter((item) => {
      const matchGenre = genre === ALL || item.genres.includes(genre);
      const matchRegion = region === ALL || item.region === region;
      const matchYear = year === ALL || String(item.year) === year;
      return matchGenre && matchRegion && matchYear;
    });
    return result.sort((a, b) => (sort === "hot" ? b.hot - a.hot : b.year - a.year || b.hot - a.hot));
  }, [items, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const activeFilterCount = [genre, region, year].filter((value) => value !== ALL).length;

  const resetFilters = () => {
    setGenre(ALL);
    setRegion(ALL);
    setYear(ALL);
    setSort("latest");
    setPage(1);
  };

  const update = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[248px_minmax(0,1fr)] gap-5 md:gap-6 items-start">
      <aside className="w-full">
        <button
          type="button"
          onClick={() => setFiltersOpen((prev) => !prev)}
          aria-expanded={filtersOpen}
          className="lg:hidden w-full flex items-center justify-between h-11 px-3.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-base w-4 h-4 flex items-center justify-center text-primary-600" />
            筛选条件
            {activeFilterCount > 0 && (
              <span className="px-1.5 rounded-full bg-accent-500 text-background-50 text-[11px] font-label">
                {activeFilterCount}
              </span>
            )}
          </span>
          <i
            className={`${filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base w-5 h-5 flex items-center justify-center text-foreground-400`}
          />
        </button>

        <div
          className={`${filtersOpen ? "block" : "hidden"} lg:block mt-3 lg:mt-0 rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-20`}
        >
          <div className="hidden lg:flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-foreground-950 font-heading">筛选条件</p>
            <button
              type="button"
              onClick={resetFilters}
              className="text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置
            </button>
          </div>
          <div className="space-y-4">
            <FilterGroup title="类型" options={genreOptions} value={genre} onChange={update(setGenre)} />
            <div className="h-px bg-background-100" />
            <FilterGroup title="地区" options={regionOptions} value={region} onChange={update(setRegion)} />
            <div className="h-px bg-background-100" />
            <FilterGroup title="年份" options={yearOptions} value={year} onChange={update(setYear)} />
          </div>
          <div className="lg:hidden mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={resetFilters}
              className="flex-1 h-9 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              重置条件
            </button>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              className="flex-1 h-9 rounded-md bg-primary-700 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              查看结果
            </button>
          </div>
        </div>

        <div className="hidden lg:block mt-3 rounded-lg border border-background-200 bg-secondary-50 p-4">
          <p className="text-xs tracking-[0.14em] text-secondary-900 font-label uppercase mb-1.5">小提示</p>
          <p className="text-xs text-foreground-600 leading-relaxed">
            同时选择多个条件可快速缩小范围。切换类型或年份后，列表会自动回到第一页。
          </p>
        </div>
      </aside>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 pb-3 mb-4 border-b border-background-200">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-foreground-950">{filtered.length}</span> 部{categoryLabel}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={resetFilters}
                className="ml-2 text-xs text-accent-700 hover:text-accent-600 cursor-pointer underline underline-offset-2"
              >
                清除筛选
              </button>
            )}
          </p>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100">
            {[
              { key: "latest" as SortMode, label: "按上映时间" },
              { key: "hot" as SortMode, label: "按热度" },
            ].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  setSort(option.key);
                  setPage(1);
                }}
                className={`px-3 h-7 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  sort === option.key
                    ? "bg-background-50 text-primary-700 font-medium"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 px-6 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-300" />
            <p className="text-base font-medium text-foreground-900 mt-3">没有符合条件的{categoryLabel}</p>
            <p className="text-sm text-foreground-500 mt-1">试试减少筛选条件，或直接查看全部内容。</p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-700 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-sm w-4 h-4 flex items-center justify-center" />
              重置筛选
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {pageItems.map((item) => (
              <TitleCard key={item.id} item={item} />
            ))}
          </div>
        )}

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}